import React, { useState, useEffect, useRef } from 'react'
import { createFormBag, FormBag, FormData, FormUpdateEvent, FormErrors, validateFormBag } from 'react-formage'

import { Button, ButtonGroup } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Modal } from 'components/ui/modal'
import { StatusGuard } from 'components/ui/status-guard'

import { sharedActionCreators, SharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore } from 'domains/org/getty/store'

import { VisitorInfoPage, Amenity } from 'generated/mos/org/getty/visitorinfo'
import { CreateAmenityRequest } from 'generated/mos/org/getty/visitorinfomanagement'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'

import { amenitySchema } from '../../../schemas/visitorInfo'

type Step = {
  header: React.ReactNode;
  body: React.ReactNode;
  footer: React.ReactNode;
};

export type Steps = {
  selectPageType: Step;
  createContentPage: Step;
  createImagePage: Step;
  createHTMLPage: Step;
  createURLPage: Step;
  createAppPage: Step;
};

export type FormValues = {
  title: string;
};

export const validate = (values: FormValues) => {
  const errors: FormErrors<FormValues> = {};
  if (!values.title.trim()) {
    errors.title = 'Title required';
  }
  return errors;
};

type ConnectedProps = {
  amenityCreate: GettyStore['getty']['amenityCreate'];
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyCreateAmenityRequest'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type DirectProps = {
  readonly onCancel: () => void;
  readonly visitorInfoRef: VisitorInfoPage.Ref;
  readonly projectId: string;
  readonly historyPush: (id: string) => void;
}

type Props = ConnectedProps & ActionProps & DirectProps;

const AmenityCreateView = (props: Props) => {
  const {
    gettyCreateAmenityRequest,
    amenityCreate,
    visitorInfoRef,
    onCancel,
    historyPush,
    projectId
  } = props

  const [ bag, setFormBag ] = useState<FormBag<FormValues>>(createFormBag({ title: '' }))

  const hasSubmitted = useRef(false)

  useEffect(() => {
    if(amenityCreate.status === Status.Ready && hasSubmitted.current && Amenity.mustRef(amenityCreate.data.ref)) {
      hasSubmitted.current = false
      historyPush(`/project/${projectId}/visitor-info/amenity/${Amenity.mustRef(amenityCreate.data.ref).id}`)
    }
  }, [amenityCreate, historyPush, projectId])

  const submit = () => {
    const validatedBag = validateFormBag(bag, validate)
    setFormBag(validatedBag)
    if (validatedBag.valid) {
      hasSubmitted.current = true
      gettyCreateAmenityRequest({
        ...CreateAmenityRequest.defaults,
        amenityCategoryRef: visitorInfoRef,
        name: amenitySchema.name ? amenitySchema.name.write(bag.values.title) : bag.values.title,
      })
    }
  }

  const onUpdate = (event: FormUpdateEvent<FormValues>) => {
    setFormBag(event.bag)
  }

  return (
    <Modal
      onClose={onCancel}
      header={<h3>Create a new section</h3>}
      footer={
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!bag.valid}
            onClick={submit}
          >
            Create Section
          </Button>
        </ButtonGroup>
      }
    >
      <StatusGuard status={amenityCreate} mode="form">
        <form noValidate onSubmit={event => {event.preventDefault()}}>
          <FormData bag={bag} onUpdate={onUpdate} validate={validate}>
            <Input
              label="Title"
              field="title"
              error={bag.touched.title ? bag.errors.title : undefined}
            />
          </FormData>
        </form>
      </StatusGuard>
    </Modal>
  )
}

export const AmenityCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  store => pick(store.getty, 'amenityCreate'),
  {
    ...pick(actionCreators, 'gettyCreateAmenityRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(AmenityCreateView)
